import type { DateTime } from "luxon";
import { useCallback } from "react";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { shiftWindowKey, shiftWindowKeyToDisplayName } from "@/downloads/dailyPrintout/util/shiftTimeFormat";
import { useGetResolverJobDisplayName } from "@/hooks/useJobDisplayName";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import type { BaseRequest, NurseFullRole } from "@/utils/models";
import { getRoleGroups } from "@/utils/roles";
import type { GapsShiftGroup } from "../gapsDayModel";

/** What a request's chip says it is for, in the shape the home's section badge (`GapsJobBadge`) draws. */
export type GapsRequestSection = Pick<GapsShiftGroup, "jobLabel" | "isJobSection" | "jobId">;

/**
 * How the gaps screens name a posted request: its hours in the home's compact form (`7a-3p`, the group headers' own
 * notation) and the section(s) it is for, as `RequestItem` resolves them — the request's job when the department posts
 * by job, else the role groups its roles make up. A request with neither (roles that form no group) has no section to
 * name, and the caller leaves the chip off rather than inventing one.
 */
export const useGapsRequestLabels = () => {
	const { chosenDepartment, timezone } = useHospitalData();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const resolveJobDisplayName = useGetResolverJobDisplayName();

	const sectionsOf = useCallback(
		(request: BaseRequest): GapsRequestSection[] => {
			if (jobManagementEnabled && request.job_id) {
				const jobLabel = resolveJobDisplayName(request.job_id);
				return jobLabel === "" ? [] : [{ jobLabel, isJobSection: true, jobId: request.job_id }];
			}
			// A request's roles are stored as full role names, the values `NurseFullRole` holds (as `useCanPostRequestToAgency` reads them).
			return getRoleGroups(request.message.roles as NurseFullRole[], chosenDepartment).map((jobLabel) => ({ jobLabel, isJobSection: false }));
		},
		[jobManagementEnabled, resolveJobDisplayName, chosenDepartment],
	);

	// In the department's zone, as every staffing screen prints a shift: a phone in another zone must not shift the hours.
	const hoursLabel = useCallback(
		(start: DateTime, end: DateTime): string => shiftWindowKeyToDisplayName(shiftWindowKey(start.setZone(timezone), end.setZone(timezone))),
		[timezone],
	);
	const timeLabelOf = useCallback((request: BaseRequest): string => hoursLabel(request.message.shift_start, request.message.shift_end), [hoursLabel]);
	// A request's heading, as the posted and approve sheets title it: its first section's job before its hours ("Nurse
	// 7a-7p"), or the hours alone for a request that names no section.
	const headingOf = useCallback(
		(request: BaseRequest): string => {
			const [section] = sectionsOf(request);
			return section === undefined ? timeLabelOf(request) : `${section.jobLabel} ${timeLabelOf(request)}`;
		},
		[sectionsOf, timeLabelOf],
	);

	return { sectionsOf, timeLabelOf, hoursLabel, headingOf };
};
